import { Component } from 'react'

import { Checkbox } from '@joor/design-system'
import get from 'lodash/get'
import PropTypes from 'prop-types'
import { Link } from 'react-router-dom'

import { OrderStatus } from '__generated__/atlas-types'

import { formatDate, isDateAfter } from 'utils/dates'
import {
  getEntriesToFetch,
  getLastPageFetched,
  getPage,
  getTotalPages,
  hasNextPage,
} from 'utils/pagination'
import { toGlobalId } from 'utils/transformations/graphql'

import {
  IN_PROGRESS_STATUS,
  NOTES_STATUS,
  PENDING_STATUS,
} from 'shop/orders/constants'

import {
  ActionBar,
  Button,
  Divider,
  Dropdown,
  Grid,
  Header,
  Loader,
  Pagination,
} from 'components/Core'
import { BLUE, ORANGE, YELLOW } from 'components/Core/Table/Indicator'
import AssignRetailerAccountModal from 'modals/OrderList/AssignRetailerAccountModal'
import AssignPublicRetailerAccountModal from 'modals/OrderList/AssignRetailerAccountModal/AssignPublicRetailerAccountModal'
import ChangeSalesRepModal from 'modals/OrderList/ChangeSalesRepModal'
import ChangeStatusModal from 'modals/OrderList/ChangeStatusModal'
import DownloadOrderModal from 'modals/OrderList/DownloadOrderModal'
import DuplicateOrdersModal from 'modals/OrderList/DuplicateOrdersModal'
import { duplicateOrders } from 'modals/OrderList/DuplicateOrdersModal/DuplicateOrdersModal.request'
import EditEventModal from 'modals/OrderList/EditEventModal'
import ExportDataModal from 'modals/OrderList/ExportDataModal'
import MarkAsExportedModal from 'modals/OrderList/MarkAsExportedModal'
import OrderImportModal from 'modals/OrderList/OrderImportModal/OrderImportModal'
import ResendEmailsModal from 'modals/OrderList/ResendEmailsModal'
import ScheduleForExportModal from 'modals/OrderList/ScheduleForExportModal'
import SendInvoiceModal from 'modals/OrderList/SendInvoiceModal'
import StartOrderModal from 'modals/OrderList/StartOrderModal'
import UpdateOrderPricingModal from 'modals/OrderList/UpdateOrderPricingModal'
import {
  ASSIGN_RETAILER_ACCOUNT,
  CHANGE_SALES_REP,
  CHANGE_STATUS,
  DUPLICATE_ORDERS,
  EDIT_EVENT,
  MARK_AS_EXPORTED,
  MERGE_ORDERS,
  REMOVE_STYLE_COLORS_WITH_NO_QUANTITIES,
  RESEND_EMAILS,
  SCHEDULE_FOR_EXPORT,
  SEND_INVOICE,
  SEND_TO_ASSORTMENT,
  SET_TO_RE_EXPORT,
  UPDATE_ORDER_PRICING,
} from 'modals/OrderList/constants'
import { REMOVE_STYLE_COLORS_WITH_NO_QUANTITIES_PAGE } from 'pages/OrderListPage/constants'
import { CANCEL_STYLE, ORDERS_CART } from 'routes/paths'

import styles from './OrderList.component.module.css'
import { HeaderIds, PaginationNavbarIds } from './OrderList.ids'
import messages from './OrderList.messages'
import BrandOrderTable from 'containers/OrderList/OrderTable/OrderTable.container'
import RetailerOrderTable from 'containers/OrderList/OrderTable/RetailerOrderTable.container'
import OrderListFilters from 'containers/OrderListFilters/OrderListFilters.container'
import StatusFilters from 'containers/StatusFilters/StatusFilters.container'
import SendPaymentRequestModal from 'features/Orders/Review/modals/SendPaymentRequestModal/SendPaymentRequestModal'
import { formatOrderAddress } from 'features/Orders/Review/utils/addresses.utils'

export default class OrderList extends Component {
  state = {
    activePage: 1,
    importOrderModalOpen: false,
    isSendPaymentRequestModalOpen: false,
  }

  componentDidUpdate = ({
    appliedFilters: prevAppliedFilters,
    pageSize: prevPageSize,
  }) => {
    const { appliedFilters, pageSize } = this.props

    if (
      prevAppliedFilters.createdByRetailer !==
        appliedFilters.createdByRetailer ||
      pageSize !== prevPageSize
    ) {
      this.handlePaginationReset()
    }
  }

  getActions = () => {
    const {
      openDynamicModal,
      orders,
      filterSelectedOrders,
      orderExportIntegrations,
      setPageLoaderActive,
      salesReps,
      refetchStaticCounts,
      handleEditEvents,
      orderEvents,
      currentUser,
      renderSuccessNotification,
      renderErrorNotification,
      selectedOrdersIds,
      totalCount,
      flags: { joorPayUseReach },
      permissions: {
        allowInvoiceRetailers,
        restrictAddEditEvents,
        allowSetToReExportOrder,
        allowMarkOrderExported,
        restrictMergeOrders,
      },
      isConnectedToProvider,
      trackClickSendInvoices,
      trackClickChangeStatus,
      trackClickCopyOrders,
      trackOpenMoveProducts,
      canAssignOrdersToNewConnections,
    } = this.props
    const { renderActionsToasterMessage, handlePaginationReset } = this

    const selectedOrders = filterSelectedOrders(orders)

    const hasOrdersWithNoQuantities = this.hasOrdersWithNoQuantities()

    const actions = [
      {
        id: PaginationNavbarIds.SendInvoiceOption,
        key: SEND_INVOICE,
        text: messages.sendInvoice,
        disabled:
          !selectedOrders.length ||
          selectedOrders.some(
            (order) => order.status === OrderStatus.CANCELLED,
          ) ||
          selectedOrders.some((order) => order.isUnassigned),
        visible:
          joorPayUseReach && allowInvoiceRetailers && isConnectedToProvider,
        handler: () => {
          if (selectedOrders?.length === 1) {
            trackClickSendInvoices(1)
            this.setState({ isSendPaymentRequestModalOpen: true })
            return
          }

          trackClickSendInvoices(selectedOrders.length)
          openDynamicModal({
            component: SendInvoiceModal,
            selectedOrders: selectedOrders.map((order) => ({
              ...order,
              billingAddress:
                formatOrderAddress({
                  firstAddress: order.billingFirstAddress,
                  secondAddress: order.billingSecondAddress,
                  city: order.billingCity,
                  country: order.billingCountry,
                  zip: order.billingZip,
                }) || undefined,
              retailerEmail: order.shippingEmail,
              currency: order?.priceType?.currency?.code,
              amount: order.remainingBalance,
            })),
          })
        },
      },
      {
        id: PaginationNavbarIds.ChangeSaleRepsOption,
        key: CHANGE_SALES_REP,
        text: messages.changeSalesRep,
        disabled: false,
        visible: true,
        handler: () =>
          openDynamicModal({
            component: ChangeSalesRepModal,
            handleResetOrder: this.handleResetOrder,
            setPageLoaderActive,
            renderActionsToasterMessage,
            renderErrorNotification,
            totalCount,
            salesReps,
          }),
      },
      {
        id: PaginationNavbarIds.ChangeStatusOption,
        key: CHANGE_STATUS,
        text: messages.changeStatus,
        disabled: !selectedOrders.length,
        visible: true,
        handler: () => {
          trackClickChangeStatus(selectedOrders.length)
          openDynamicModal({
            component: ChangeStatusModal,
            handleResetOrder: this.handleResetOrder,
            selectedOrders,
            setPageLoaderActive,
            refetchStaticCounts,
            renderActionsToasterMessage,
          })
        },
      },
      {
        id: PaginationNavbarIds.EditEventOption,
        key: EDIT_EVENT,
        text: messages.editEvent,
        disabled: !selectedOrders.length || restrictAddEditEvents,
        visible: true,
        handler: () =>
          openDynamicModal({
            component: EditEventModal,
            handleResetOrder: this.handleResetOrder,
            orderEvents,
            selectedOrders,
            handleEditEvents,
            setPageLoaderActive,
            renderActionsToasterMessage,
          }),
      },
      {
        id: PaginationNavbarIds.ResendEmailsOption,
        key: RESEND_EMAILS,
        text: messages.resendEmails,
        disabled: !selectedOrders.length,
        visible: true,
        handler: () =>
          openDynamicModal({
            component: ResendEmailsModal,
            handleResetOrder: this.handleResetOrder,
            currentUser,
            selectedOrders,
            setPageLoaderActive,
            renderSuccessNotification,
            renderErrorNotification,
          }),
      },
      {
        id: PaginationNavbarIds.UpdateOrderPricingOption,
        key: UPDATE_ORDER_PRICING,
        text: messages.updatePricing,
        disabled: false,
        visible: true,
        handler: () =>
          openDynamicModal({
            component: UpdateOrderPricingModal,
            handleResetOrder: this.handleResetOrder,
            renderActionsToasterMessage,
            totalCount,
          }),
      },
      {
        id: PaginationNavbarIds.SetToReExportOption,
        key: SET_TO_RE_EXPORT,
        text: messages.reExport,
        disabled: !selectedOrders.length || !allowSetToReExportOrder,
        visible: this.accountHasOrderExportIntegrations(),
        handler: () => this.handleActionSubmit(this.props.handleSetToReexport),
      },
      {
        id: PaginationNavbarIds.MarkExportedOption,
        key: MARK_AS_EXPORTED,
        text: messages.markAsExported,
        disabled: !selectedOrders.length || !allowMarkOrderExported,
        visible: this.accountHasOrderExportIntegrations(),
        handler: () =>
          openDynamicModal({
            component: MarkAsExportedModal,
            handleResetOrder: this.handleResetOrder,
            renderActionsToasterMessage,
            handlePaginationReset,
            orderExportIntegrations,
            setPageLoaderActive,
            selectedOrdersIds,
          }),
      },
      {
        id: PaginationNavbarIds.ScheduledForExportOption,
        key: SCHEDULE_FOR_EXPORT,
        text: messages.scheduleExport,
        disabled: !selectedOrders.length,
        visible: this.accountHasManuallyScheduledIntegrations(),
        handler: () =>
          openDynamicModal({
            component: ScheduleForExportModal,
            handleResetOrder: this.handleResetOrder,
            renderActionsToasterMessage,
          }),
      },
      {
        id: PaginationNavbarIds.DuplicateOption,
        key: DUPLICATE_ORDERS,
        text: messages.duplicateOrders,
        disabled: !selectedOrders.length,
        visible: true,
        handler: () => {
          trackClickCopyOrders(selectedOrders.length)
          openDynamicModal({
            component: DuplicateOrdersModal,
            handleResetOrder: this.handleResetOrder,
            selectedOrders,
          })
        },
      },
      {
        id: PaginationNavbarIds.MoveProductsOption,
        key: MERGE_ORDERS,
        text: messages.mergeOrders,
        disabled: selectedOrders.length < 2,
        visible: !restrictMergeOrders,
        handler: () => {
          this.props.toggleCheckMergeOrders(true)
          this.props.checkMergeCriteria()
          trackOpenMoveProducts(selectedOrders.length)
        },
      },
      {
        id: PaginationNavbarIds.AssignRetailerOption,
        key: ASSIGN_RETAILER_ACCOUNT,
        text: messages.assignRetailerAccount,
        disabled: !selectedOrders.length,
        visible: true,
        handler: () =>
          openDynamicModal({
            component: canAssignOrdersToNewConnections
              ? AssignPublicRetailerAccountModal
              : AssignRetailerAccountModal,
            handleResetOrder: this.handleResetOrder,
            renderActionsToasterMessage: this.renderActionsToasterMessage,
            selectedOrdersIds,
            setPageLoaderActive,
          }),
      },
      {
        id: PaginationNavbarIds.RemoveStyleColorNoQuantitiesOption,
        key: REMOVE_STYLE_COLORS_WITH_NO_QUANTITIES,
        text: messages.removeStyleColors,
        disabled: !hasOrdersWithNoQuantities,
        visible: true,
        handler: hasOrdersWithNoQuantities
          ? () =>
              this.handleRemoveStyleColorWithoutQuantities(
                {
                  text: messages.removeStyleColors,
                },
                selectedOrders.length,
              )
          : null,
      },
    ]

    return actions
      .filter((action) => action.visible)
      .map(({ handler, visible: _visible, ...action }) => ({
        ...action,
        onClick: (...actionArgs) =>
          this.handleActionClick(action, handler, actionArgs),
      }))
  }

  handleDuplicateOrders = async () => {
    const {
      accountId,
      accountName,
      filterSelectedOrders,
      navigate,
      renderToasters: { renderErrorToast, renderSuccessToast },
      orders,
      setPageLoaderActive,
    } = this.props

    const selectedOrders = filterSelectedOrders(orders)
    const selectedOrdersIds = selectedOrders.map(
      (selectedOrder) => selectedOrder.webOrderLookupId,
    )

    setPageLoaderActive(true)
    try {
      await duplicateOrders({
        orderIds: selectedOrdersIds,
        retailer: {
          id: accountId,
          displayName: accountName,
        },
      })
      this.handleResetOrder()
      renderSuccessToast({
        title: 'Orders duplicated',
        description: 'Orders have been successfully duplicated.',
      })
      navigate(ORDERS_CART)
    } catch (_) {
      renderErrorToast({
        title: 'Unable to duplicate Order(s)',
        description: 'An unexpected server error has ocurred',
        action: {
          label: 'Try Again',
          handler: () => this.handleDuplicateOrders(),
        },
      })
    } finally {
      setPageLoaderActive(false)
    }
  }

  handleSendToAssortment = async () => {
    const {
      selectedOrdersIds,
      asyncAddOrdersToVA,
      setPageLoaderActive,
      handleResetSelectedOrders,
      filterSelectedOrders,
      orders,
    } = this.props

    if (!selectedOrdersIds || selectedOrdersIds.length === 0) {
      return
    }

    // Get selected orders and filter valid ones (NOTES, PENDING, APPROVED, SHIPPED)
    // to calculate the count for the toast message
    const selectedOrders = filterSelectedOrders(orders)
    const validOrderStatuses = [
      OrderStatus.ALL_NOTES,
      OrderStatus.PENDING,
      OrderStatus.APPROVED,
      OrderStatus.SHIPPED,
    ]
    const validOrders = selectedOrders.filter((order) =>
      validOrderStatuses.includes(order.status),
    )

    const totalOrders = selectedOrders.length
    const validOrdersCount = validOrders.length

    setPageLoaderActive(true)

    try {
      // Send all selected order IDs to the mutation
      // The backend will only process the valid ones
      await asyncAddOrdersToVA(
        {
          orderIds: selectedOrdersIds,
        },
        {
          totalOrders,
          validOrders: validOrdersCount,
        },
      )

      handleResetSelectedOrders()
    } catch (error) {
      console.error('[OrderList] Error sending to assortment', error)
    } finally {
      setPageLoaderActive(false)
    }
  }

  getRetailerActions = () => {
    const {
      filterSelectedOrders,
      orders,
      openDynamicModal,
      refetchStaticCounts,
      setPageLoaderActive,
      trackClickChangeStatus,
      trackClickCopyOrders,
      hasVAAccess,
      selectedOrdersIds,
    } = this.props

    const selectedOrders = filterSelectedOrders(orders)

    const hasOrdersWithNoQuantities = this.hasOrdersWithNoQuantities()

    const actions = [
      {
        key: CHANGE_STATUS,
        text: messages.changeStatus,
        disabled:
          !selectedOrders.length ||
          selectedOrders.some(
            ({ status }) =>
              ![
                IN_PROGRESS_STATUS.id,
                NOTES_STATUS.id,
                PENDING_STATUS.id,
              ].includes(status),
          ),
        visible: true,
        handler: () => {
          trackClickChangeStatus(selectedOrders.length)
          openDynamicModal({
            component: ChangeStatusModal,
            handleResetOrder: this.handleResetOrder,
            selectedOrders,
            setPageLoaderActive,
            refetchStaticCounts,
            renderActionsToasterMessage: this.renderActionsToasterMessage,
            isRetailer: true,
          })
        },
      },
      {
        key: DUPLICATE_ORDERS,
        text: messages.duplicateOrders,
        disabled: !selectedOrders.length,
        visible: true,
        handler: () => {
          trackClickCopyOrders(selectedOrders.length)
          this.handleDuplicateOrders()
        },
      },
      {
        key: REMOVE_STYLE_COLORS_WITH_NO_QUANTITIES,
        text: messages.removeStyleColors,
        disabled: !hasOrdersWithNoQuantities,
        visible: true,
        handler: hasOrdersWithNoQuantities
          ? () =>
              this.handleRemoveStyleColorWithoutQuantities(
                {
                  text: messages.removeStyleColors,
                },
                selectedOrders.length,
              )
          : null,
      },
      {
        id: PaginationNavbarIds.SendToAssortmentOption,
        key: SEND_TO_ASSORTMENT,
        text: messages.sendToAssortment,
        disabled:
          !selectedOrdersIds || selectedOrdersIds.length === 0 || !hasVAAccess,
        visible: true,
        handler: () => {
          this.handleSendToAssortment()
        },
      },
    ]
    return actions
      .filter((action) => action.visible)
      .map(({ handler, visible: _visible, ...action }) => ({
        ...action,
        onClick: (...actionArgs) =>
          this.handleActionClick(action, handler, actionArgs),
      }))
  }

  getCreatedByRetailerTooltipMessage = (orderCreatedAt) => {
    const { dateFormat } = this.props
    const formattedCreatedAt = formatDate(orderCreatedAt, dateFormat)

    return messages.createdByRetailerTooltipBody(formattedCreatedAt)
  }

  getOrdersPageWithIndicator = (orders) => {
    const { pageSize } = this.props
    const paginatedOrders = getPage(orders, this.state.activePage, pageSize)
    return paginatedOrders.map((order) => ({
      ...order,
      indicatorInfo: this.getRowIndicators(order),
    }))
  }

  getRowIndicators = (order) => {
    let indicatorInfo = []
    if (order.createdByRetailer) {
      indicatorInfo = [
        ...indicatorInfo,
        {
          color: BLUE,
          tooltip: this.getCreatedByRetailerTooltipMessage(order.createdAt),
        },
      ]
    }
    if (order.isUnassigned) {
      indicatorInfo = [
        ...indicatorInfo,
        {
          color: order.orphanRetailer ? ORANGE : YELLOW,
          tooltip: order.orphanRetailer
            ? messages.connectionPendingTooltip
            : messages.unassignedOrdersTooltip,
        },
      ]
    }
    return indicatorInfo
  }

  isOrderNewerThanLastLogin = (order) => {
    const { currentUser } = this.props
    return isDateAfter(order.createdAt, currentUser.lastLogin)
  }

  isNewRetailerOrder = (order) => {
    const isRetailerOrder = get(order, 'createdByRetailer', false)

    return isRetailerOrder && this.isOrderNewerThanLastLogin(order)
  }

  accountHasManuallyScheduledIntegrations = () =>
    this.props.orderExportIntegrations.some(
      (integration) => integration.isManuallyScheduled,
    )

  accountHasOrderExportIntegrations = () =>
    !!this.props.orderExportIntegrations.length

  hasOrdersWithNoQuantities = () => {
    const { orders, selectedOrdersIds } = this.props
    if (!orders.length || !selectedOrdersIds.length) {
      return false
    }
    const ordersSelected = orders.filter(
      (order) => selectedOrdersIds.indexOf(order.id) !== -1,
    )
    return ordersSelected.some((order) =>
      get(order, 'hasEmptyStyleColors', false),
    )
  }

  handleActionClick = (action, handler, actionArgs) => {
    if (action.disabled) {
      return
    }

    this.props.trackActionClick({ action })

    handler(actionArgs)
  }

  handleActionSubmit = (handler) => {
    this.props.setPageLoaderActive(true)
    const selectedOrdersIds = [...this.props.selectedOrdersIds]
    return handler(selectedOrdersIds)
      .then(({ data }) => {
        const mutationNameKey = Object.keys(data)[0]
        const returnedOrders = data[mutationNameKey]

        this.handleResetOrder()
        this.props.setPageLoaderActive(false)
        this.renderActionsToasterMessage(
          returnedOrders.updatedOrderIds.length,
          selectedOrdersIds.length - returnedOrders.updatedOrderIds.length,
        )
      })
      .catch((error) => {
        console.error(error)
        this.props.setPageLoaderActive(false)
      })
  }

  handlePaginationReset = () => this.setState(() => ({ activePage: 1 }))

  handlePaginationChange = (_, { activePage }) => {
    const {
      ordersQueryLoading,
      trackPaginationChange,
      orders,
      totalCount,
      loadMoreEntries,
      pageSize,
    } = this.props

    if (ordersQueryLoading) {
      return
    }

    trackPaginationChange({ activePage })

    const lastPageFetched = getLastPageFetched(orders, pageSize)
    const isAfterLastPageFetched = lastPageFetched < activePage

    if (
      isAfterLastPageFetched &&
      hasNextPage(activePage - 1, totalCount, pageSize)
    ) {
      const numberOfEntries = getEntriesToFetch(
        activePage,
        lastPageFetched,
        pageSize,
      )
      loadMoreEntries(numberOfEntries)
    }

    this.setState({
      activePage,
    })
  }

  handleExportAction = (action, successMessage, failMessage, taskId) => {
    const {
      renderSuccessNotification,
      renderErrorNotification,
      renderNotification,
      listOrderExportTasks,
    } = this.props

    if (taskId) {
      listOrderExportTasks({
        variables: {
          input: {
            ids: [taskId],
          },
        },
      })
      return
    }
    renderNotification(messages.waitForExport)

    action()
      .then(() => {
        renderSuccessNotification(successMessage)
        this.handleResetOrder()
      })
      .catch((error) => {
        console.error(error)
        renderErrorNotification(failMessage)
      })
  }

  handleResetOrder = (refetch = true) => {
    this.handlePaginationReset()
    this.props.handleResetSelectedOrders()
    if (refetch) {
      this.props.refetchOrders()
    }
  }

  openImportOrdersModal = () => {
    this.props.trackOpenImportOrdersModal()
    this.setState({ importOrderModalOpen: true })
  }

  closeImportOrdersModal = () => {
    this.setState({ importOrderModalOpen: false })
  }

  openStartOrderModal = () => {
    const {
      openDynamicModal,
      currentUser: { account: accountId },
      setPageLoaderActive,
      trackOpenStartOrderModal,
    } = this.props

    trackOpenStartOrderModal()

    openDynamicModal({
      component: StartOrderModal,
      accountId,
      setPageLoaderActive,
    })
  }

  openDownloadOrderModal = () => {
    const {
      openDynamicModal,
      selectedOrdersIds,
      orderExportIntegrations,
      currentUser: {
        account: { accountId },
      },
      trackDownloadOrderModal,
    } = this.props

    trackDownloadOrderModal()

    openDynamicModal({
      component: DownloadOrderModal,
      handleExportAction: this.handleExportAction,
      accountId,
      selectedOrdersIds,
      orderExportIntegrations,
    })
  }

  openExportDataModal = () => {
    const {
      selectedOrdersIds,
      openDynamicModal,
      orderExportIntegrations,
      appliedFilters,
      trackOpenExportDataModal,
    } = this.props

    const statusFilter = appliedFilters.status
      .map((filterInfo) => filterInfo.id)
      .filter(
        (filterId) =>
          !['NOTES_WITH_QUANTITIES', 'NOTES_WITHOUT_QUANTITIES'].includes(
            filterId,
          ),
      )

    trackOpenExportDataModal()

    openDynamicModal({
      component: ExportDataModal,
      handleExportAction: this.handleExportAction,
      selectedOrdersIds,
      orderExportIntegrations,
      statusFilter,
    })
  }

  handleRemoveStyleColorWithoutQuantities = (action, count) => {
    const {
      onChangeCurrentPage,
      trackActionClick,
      trackClickRemoveStyleColorWithoutQuantities,
    } = this.props

    trackActionClick({ action })
    trackClickRemoveStyleColorWithoutQuantities(count)
    onChangeCurrentPage(REMOVE_STYLE_COLORS_WITH_NO_QUANTITIES_PAGE)
  }

  handleClickChangePageSize = (size) => {
    this.props.trackPageSizeChange(size)
    this.props.changePageSize(size)
  }

  renderTotalCount = (totalCount, selectedOrders) => {
    const selectedOrdersLength = selectedOrders.length
    return (
      <div className={styles.total}>
        {messages.selectedOrders(selectedOrdersLength, totalCount)}
      </div>
    )
  }

  renderActionsToasterMessage = (successfulCount, erroredCount) => {
    const { renderErrorNotification, renderSuccessNotification } = this.props
    const successMessage = messages.successMessage(successfulCount)
    const totalCount = successfulCount + erroredCount
    const partialErrorMessage = messages.partialErrorMessage(
      erroredCount,
      totalCount,
    )
    const errorMessage = messages.errorMessage(erroredCount)
    if (erroredCount === 0) {
      return renderSuccessNotification(successMessage)
    } else if (successfulCount === 0) {
      return renderErrorNotification(errorMessage)
    }
    return renderErrorNotification(partialErrorMessage)
  }

  renderCancelStyleButton = (extraButtonProps) => {
    const {
      permissions: { restrictCancelStyles },
      trackClickCancelReplaceButton,
    } = this.props

    if (restrictCancelStyles) {
      return (
        <Button {...extraButtonProps} withGrid disabled>
          {messages.cancelReplaceStyle}
        </Button>
      )
    }

    return (
      <Link to={CANCEL_STYLE} style={{ color: 'inherit' }}>
        <Button
          id={PaginationNavbarIds.CancelStylesButton}
          {...extraButtonProps}
          withGrid
          onClick={trackClickCancelReplaceButton}
        >
          {messages.cancelReplaceStyle}
        </Button>
      </Link>
    )
  }

  renderDownloadOrderModal = (extraButtonProps) => {
    const { listOrderExportTasksLoading } = this.props

    return (
      <Button
        id={PaginationNavbarIds.DownloadConfirmationButton}
        {...extraButtonProps}
        withGrid
        disabled={listOrderExportTasksLoading}
        onClick={this.openDownloadOrderModal}
      >
        {messages.downloadOrderConfirmation}
      </Button>
    )
  }

  renderExportDataButton = (extraButtonProps) => {
    const { ordersQueryLoading } = this.props

    return (
      <Button
        id={PaginationNavbarIds.ExportOrdersButton}
        {...extraButtonProps}
        withGrid
        disabled={ordersQueryLoading}
        onClick={this.openExportDataModal}
      >
        {messages.exportData}
      </Button>
    )
  }

  renderOrderActions = () => {
    const {
      isBrandUser,
      flags: { orderListUiEnhancements },
    } = this.props

    const actions = isBrandUser ? this.getActions() : this.getRetailerActions()

    return (
      <Grid.Col sm={2} md={2} lg={2}>
        <Dropdown
          id={PaginationNavbarIds.ActionsButton}
          options={actions}
          text={messages.actions}
          upward={!orderListUiEnhancements}
          className={styles.actions}
        />
      </Grid.Col>
    )
  }

  render() {
    const {
      allOrdersAreSelected,
      anyRetailerCreatedOrders,
      budgetsTotalCount,
      currentUser,
      flags: { orderListUiEnhancements },
      filterSelectedOrders,
      invoices,
      invoiceStatusQueryLoading,
      isBrandUser,
      isConnectedToProvider,
      isOrderSelected,
      newRetailerOrdersCount,
      orders,
      ordersApproved,
      ordersCancelled,
      ordersDraft,
      ordersPending,
      ordersQueryLoading: loaderIsActive,
      ordersShipped,
      ordersWithQuantities,
      ordersWithoutQuantities,
      pageSize,
      selectedOrdersIds,
      toggleOrder,
      toggleSelectOrdersOnPage,
      totalCount,
      isPaymentInfoLoading,
      hasPaymentInfoErrors,
      shouldDisplayDivisionColumn,
      toggleDisplayDivisionColumn,
      orderDivisionsLoading,
    } = this.props
    const selectedOrders = filterSelectedOrders(orders)

    const pageSizes = [10, 15, 20, 30]

    const OrderTable = isBrandUser ? BrandOrderTable : RetailerOrderTable
    const extraButtonProps = {
      ...(orderListUiEnhancements ? { tertiary: true } : { primary: true }),
    }

    return (
      <>
        <div>
          <Grid>
            <Grid.Row>
              <Grid.Col className={styles.header} sm={1} md={1} lg={5}>
                <Header>{messages.header}</Header>
                {isBrandUser && (
                  <Checkbox
                    id={HeaderIds.DivisionVisibility}
                    className={styles.divisionCheckbox}
                    checked={shouldDisplayDivisionColumn}
                    onChange={() =>
                      toggleDisplayDivisionColumn(!shouldDisplayDivisionColumn)
                    }
                    label={'View Division Code'}
                  />
                )}
              </Grid.Col>
              <StatusFilters
                ordersCancelled={ordersCancelled}
                ordersApproved={ordersApproved}
                ordersShipped={ordersShipped}
                ordersWithQuantities={ordersWithQuantities}
                ordersWithoutQuantities={ordersWithoutQuantities}
                ordersPending={ordersPending}
                ordersDraft={ordersDraft}
                handlePaginationReset={this.handlePaginationReset}
              />
              {orderListUiEnhancements ? (
                this.renderOrderActions()
              ) : (
                <Button
                  id={HeaderIds.ImportOrdersButton}
                  onClick={this.openImportOrdersModal}
                  {...extraButtonProps}
                  withGrid
                >
                  {messages.importOrders}
                </Button>
              )}
              <Button
                id={HeaderIds.StartOrderButton}
                onClick={this.openStartOrderModal}
                primary
                withGrid
              >
                {messages.startOrder}
              </Button>
            </Grid.Row>
            <Grid.Row className={styles.dividerRow}>
              <Grid.Col>
                <Divider />
              </Grid.Col>
            </Grid.Row>
            <Grid.Row>
              {loaderIsActive && <Loader active={loaderIsActive} />}
              <Grid.Col>
                <OrderListFilters
                  newRetailerOrdersCount={newRetailerOrdersCount}
                  anyRetailerCreatedOrders={anyRetailerCreatedOrders}
                  handlePaginationReset={this.handlePaginationReset}
                  dateFormat={currentUser.dateFormat}
                />
                <OrderTable
                  allOrdersAreSelected={allOrdersAreSelected}
                  budgetsTotalCount={budgetsTotalCount}
                  currentUser={currentUser}
                  dateFormat={currentUser.dateFormat}
                  filterSelectedOrders={filterSelectedOrders}
                  isBrandUser={isBrandUser}
                  indicator={isBrandUser}
                  invoices={invoices}
                  invoicesReady={!invoiceStatusQueryLoading}
                  isConnectedToProvider={isConnectedToProvider}
                  handlePaginationReset={this.handlePaginationReset}
                  isOrderSelected={isOrderSelected}
                  orders={this.getOrdersPageWithIndicator(orders)}
                  selectedOrdersIds={selectedOrdersIds}
                  toggleOrder={toggleOrder}
                  toggleSelectOrdersOnPage={toggleSelectOrdersOnPage}
                  hasDraftOrders={ordersDraft > 0}
                  isPaymentInfoLoading={isPaymentInfoLoading}
                  hasPaymentInfoErrors={hasPaymentInfoErrors}
                  shouldDisplayDivisionColumn={shouldDisplayDivisionColumn}
                  orderDivisionsLoading={orderDivisionsLoading}
                />
              </Grid.Col>
            </Grid.Row>
          </Grid>
          <ActionBar>
            <Grid>
              <Grid.Row>
                {orderListUiEnhancements && (
                  <Button
                    id={HeaderIds.ImportOrdersButton}
                    onClick={this.openImportOrdersModal}
                    {...extraButtonProps}
                    withGrid
                  >
                    {messages.importOrders}
                  </Button>
                )}
                {this.renderDownloadOrderModal(extraButtonProps)}
                {this.renderExportDataButton(extraButtonProps)}
                {isBrandUser && (
                  <>{this.renderCancelStyleButton(extraButtonProps)}</>
                )}
                {!orderListUiEnhancements && this.renderOrderActions()}
                {!isBrandUser && <Grid.Col sm={2} md={2} lg={2} />}
                <Grid.Col sm={4} md={4} lg={8}>
                  <div className={styles.paginationContainer}>
                    {!!totalCount &&
                      this.renderTotalCount(totalCount, selectedOrdersIds)}
                    <Pagination
                      onPageChange={this.handlePaginationChange}
                      totalPages={getTotalPages(totalCount, pageSize)}
                      activePage={this.state.activePage}
                    />
                  </div>
                </Grid.Col>
                <Grid.Col sm={1} md={1} lg={1}>
                  <Dropdown
                    options={pageSizes.map((size) => ({
                      key: size,
                      text: size,
                      value: size,
                      disabled: size > totalCount,
                      onClick: () => this.handleClickChangePageSize(size),
                    }))}
                    upward
                    value={pageSize}
                    selection
                    className={styles.actions}
                  />
                </Grid.Col>
              </Grid.Row>
            </Grid>
          </ActionBar>
        </div>
        {this.state.importOrderModalOpen && (
          <OrderImportModal onClose={this.closeImportOrdersModal} />
        )}
        {this.state.isSendPaymentRequestModalOpen && (
          <SendPaymentRequestModal
            isOpen={this.state.isSendPaymentRequestModalOpen}
            setIsOpen={(isOpen) =>
              this.setState({
                isSendPaymentRequestModalOpen: isOpen,
              })
            }
            orderId={selectedOrders?.[0]?.id}
            orderPO={selectedOrders?.[0]?.referenceNumber}
            orderCurrency={selectedOrders?.[0]?.priceType?.currency?.code}
            retailerId={selectedOrders?.[0]?.retailer?.id}
            retailerEmail={
              selectedOrders?.[0]?.shippingEmail ||
              selectedOrders?.[0]?.retailer?.email
            }
            retailerName={selectedOrders?.[0]?.retailerName}
            webOrderId={
              selectedOrders?.[0]?.webOrderLookupId
                ? toGlobalId('OrderWeb', selectedOrders?.[0]?.webOrderLookupId)
                : undefined
            }
            brandId={selectedOrders?.[0]?.brand?.id}
            brandName={selectedOrders?.[0]?.brand?.name}
            billingAddress={formatOrderAddress({
              firstAddress: selectedOrders?.[0].billingFirstAddress,
              secondAddress: selectedOrders?.[0].billingSecondAddress,
              country: selectedOrders?.[0].billingCountry,
              city: selectedOrders?.[0].billingCity,
              zip: selectedOrders?.[0].billingZip,
            })}
          />
        )}
      </>
    )
  }
}

OrderList.propTypes = {
  allOrdersAreSelected: PropTypes.func.isRequired,
  anyRetailerCreatedOrders: PropTypes.bool,
  appliedFilters: PropTypes.object.isRequired,
  budgetsTotalCount: PropTypes.number,
  canAssignOrdersToNewConnections: PropTypes.bool,
  changePageSize: PropTypes.func,
  currentUser: PropTypes.object.isRequired,
  dateFormat: PropTypes.string,
  filterSelectedOrders: PropTypes.func.isRequired,
  flags: PropTypes.shape({
    joorPayUseReach: PropTypes.bool,
    useImportModal: PropTypes.bool,
  }),
  handleEditEvents: PropTypes.func,
  handleResetSelectedOrders: PropTypes.func.isRequired,
  handleSetToReexport: PropTypes.func,
  invoices: PropTypes.arrayOf(PropTypes.object),
  invoiceStatusQueryLoading: PropTypes.bool,
  isBrandUser: PropTypes.bool.isRequired,
  isLiteRetailerUser: PropTypes.bool.isRequired,
  isConnectedToProvider: PropTypes.bool,
  isOrderSelected: PropTypes.func.isRequired,
  loadMoreEntries: PropTypes.func.isRequired,
  newRetailerOrdersCount: PropTypes.number,
  onChangeCurrentPage: PropTypes.func,
  openDynamicModal: PropTypes.func,
  orderEvents: PropTypes.arrayOf(PropTypes.object),
  orderExportIntegrations: PropTypes.arrayOf(PropTypes.object),
  orders: PropTypes.arrayOf(PropTypes.object).isRequired,
  ordersApproved: PropTypes.number,
  ordersCancelled: PropTypes.number,
  ordersDraft: PropTypes.number,
  ordersPending: PropTypes.number,
  ordersQueryLoading: PropTypes.bool.isRequired,
  ordersShipped: PropTypes.number,
  ordersWithQuantities: PropTypes.number,
  ordersWithoutQuantities: PropTypes.number,
  pageSize: PropTypes.number,
  permissions: PropTypes.object,
  refetchOrders: PropTypes.func.isRequired,
  refetchStaticCounts: PropTypes.func,
  renderErrorNotification: PropTypes.func.isRequired,
  renderNotification: PropTypes.func.isRequired,
  renderSuccessNotification: PropTypes.func.isRequired,
  salesReps: PropTypes.arrayOf(PropTypes.object),
  selectedOrdersIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  setPageLoaderActive: PropTypes.func.isRequired,
  totalCount: PropTypes.number.isRequired,
  toggleOrder: PropTypes.func.isRequired,
  toggleSelectOrdersOnPage: PropTypes.func.isRequired,
  trackActionClick: PropTypes.func,
  trackClickCancelReplaceButton: PropTypes.func,
  trackClickSendInvoices: PropTypes.func,
  trackClickChangeStatus: PropTypes.func,
  trackClickCopyOrders: PropTypes.func,
  trackClickRemoveStyleColorWithoutQuantities: PropTypes.func,
  trackDownloadOrderModal: PropTypes.func,
  trackPaginationChange: PropTypes.func,
  trackOpenExportDataModal: PropTypes.func,
  trackOpenStartOrderModal: PropTypes.func,
  trackOpenMoveProducts: PropTypes.func,
  listOrderExportTasks: PropTypes.func,
  hasVAAccess: PropTypes.bool,
  asyncAddOrdersToVA: PropTypes.func,
  renderToasters: PropTypes.shape({
    renderErrorToast: PropTypes.func,
    renderWarningToast: PropTypes.func,
  }),
}

OrderList.defaultProps = {
  anyRetailerCreatedOrders: false,
  budgetsTotalCount: 0,
  canAssignOrdersToNewConnections: false,
  changePageSize: () => {},
  dateFormat: 'MM/DD/YYYY hh:mm A',
  flags: {
    joorPayUseReach: false,
    useImportModal: false,
  },
  handleEditEvents: () => {},
  handleSetToReexport: () => {},
  invoices: [],
  invoiceStatusQueryLoading: false,
  isConnectedToProvider: false,
  newRetailerOrdersCount: 0,
  onChangeCurrentPage: () => {},
  openDynamicModal: () => {},
  orderEvents: [],
  orderExportIntegrations: [],
  ordersApproved: null,
  ordersCancelled: null,
  ordersDraft: null,
  ordersPending: null,
  ordersShipped: null,
  ordersWithQuantities: null,
  ordersWithoutQuantities: null,
  pageSize: 0,
  permissions: {},
  refetchStaticCounts: () => {},
  salesReps: [],
  trackActionClick: () => {},
  trackClickCancelReplaceButton: () => {},
  trackClickSendInvoices: () => {},
  trackClickChangeStatus: () => {},
  trackClickCopyOrders: () => {},
  trackClickRemoveStyleColorWithoutQuantities: () => {},
  trackDownloadOrderModal: () => {},
  trackPaginationChange: () => {},
  trackOpenExportDataModal: () => {},
  trackOpenStartOrderModal: () => {},
  trackOpenMoveProducts: () => {},
  listOrderExportTasks: () => {},
  hasVAAccess: false,
  asyncAddOrdersToVA: () => {},
  renderToasters: {
    renderErrorToast: () => {},
    renderWarningToast: () => {},
  },
}
